import { useAuth } from '../../../contexts/AuthContext';
import { LoadingState } from '../../ui/loading-state';
import { 
  Dialog, 
  DialogContent, 
  DialogDescription, 
  DialogHeader, 
  DialogTitle,
  DialogFooter
} from '../../ui/dialog';
import { Button } from '../../ui/button';
import { useState } from 'react';
import { useUpdateUser } from '../../../lib/supabase/users/supabaseUserService';
import { toast } from 'react-hot-toast';
import { countries } from 'countries-list';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "../../ui/popover";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "../../ui/command";
import { Check, ChevronsUpDown, AlertCircle } from 'lucide-react';
import { cn } from '../../../lib/utils';
import { Checkbox } from '../../ui/checkbox';
import { Label } from '../../ui/label';
import { Alert, AlertDescription, AlertTitle } from '../../ui/alert';
import { DateTime } from 'luxon';

interface TaxCountryGuardProps {
  children: React.ReactNode;
}

const COUNTRIES = Object.values(countries).map(country => country.name).sort();

const TaxCountryGuard = ({ children }: TaxCountryGuardProps): JSX.Element => {
  const { userData, loading: userLoading, refetchUserData } = useAuth();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [taxCountry, setTaxCountry] = useState('');
  const [taxCountryOpen, setTaxCountryOpen] = useState(false);
  const [isCertified, setIsCertified] = useState(false);
  const updateUser = useUpdateUser();

  if (userLoading || !userData) {
    return <LoadingState />;
  }

  // If user has a tax country set, render children
  if (userData.tax?.country) {
    return <>{children}</>;
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!taxCountry) {
      toast.error('Please select a tax country');
      return;
    }

    if (!isCertified) {
      toast.error('Please certify your tax country');
      return;
    }

    setIsSubmitting(true);

    try {
      await updateUser.mutateAsync({
        id: userData.id,
        user: { 
          tax: {
            country: taxCountry,
            confirmed_at: DateTime.utc()
          }
        }
      });
      
      // Refetch user data to update the UI
      await refetchUserData();
      
      toast.success('Tax country updated successfully');
    } catch (err: any) {
      console.error('Error updating tax country:', err);
      toast.error(err.message || 'Failed to update tax country. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <Dialog open={true}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="text-brand-dark dark:text-foreground flex items-center gap-2">
              Set Your Tax Country
            </DialogTitle>
            <DialogDescription className="text-muted-foreground dark:text-muted-foreground">
              Please select the country <b>you pay tax in</b>. This ensures that your pay is calculated correctly.
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-4">
              <Popover open={taxCountryOpen} onOpenChange={setTaxCountryOpen}>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    role="combobox"
                    aria-expanded={taxCountryOpen}
                    className="w-full justify-between"
                  >
                    {taxCountry || "Select country..."}
                    <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-full p-0" align="start">
                  <Command>
                    <CommandInput placeholder="Search countries..." />
                    <CommandList>
                      <CommandEmpty>No country found.</CommandEmpty>
                      <CommandGroup>
                        {COUNTRIES.map((country) => (
                          <CommandItem
                            key={country}
                            value={country}
                            onSelect={() => {
                              setTaxCountry(country);
                              setTaxCountryOpen(false);
                            }}
                          >
                            <Check
                              className={cn(
                                "mr-2 h-4 w-4",
                                taxCountry === country ? "opacity-100" : "opacity-0"
                              )}
                            />
                            {country}
                          </CommandItem>
                        ))}
                      </CommandGroup>
                    </CommandList>
                  </Command>
                </PopoverContent>
              </Popover>

              <div className="flex items-center space-x-2">
                <Checkbox 
                  id="certify"
                  checked={isCertified}
                  onCheckedChange={(checked) => setIsCertified(checked as boolean)}
                />
                <Label
                  htmlFor="certify"
                  className="text-sm text-muted-foreground leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                >
                  I confirm this is the country I pay tax in
                </Label>
              </div>

              <Alert variant="destructive">
                <AlertCircle className="h-4 w-4" />
                <AlertTitle>Tax Country</AlertTitle>
                <AlertDescription>
                  This is the country <b>you pay tax in</b>. Upper Echelon does not withhold the tax amounts, <b>you are still responsible for paying your own taxes</b>.
                </AlertDescription>
              </Alert>
            </div>

            <DialogFooter>
              <div className="flex flex-col gap-2 sm:flex-row sm:justify-end w-full">
                <Button 
                  type="submit"
                  disabled={isSubmitting || !taxCountry || !isCertified}
                  className="w-full sm:w-auto"
                >
                  {isSubmitting ? 'Updating...' : 'Set Tax Country'}
                </Button>
              </div>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default TaxCountryGuard; 